import {
  DEBUG_PANEL_CITY_LAT,
  DEBUG_PANEL_CITY_LONG,
  getLatitude,
  setLatitude,
  getLongitude,
  setLongitude,
  getCookieTimestamp,
  setCookieTimeStamp,
  setCookieModalTimeStamp,
} from 'helpers/ContentHelper';
import { getLocationCookieDuration } from 'helpers/ConfigHelper';
import { getCookie } from 'helpers/Storage';
import _ from 'lodash';

const GEO_LOCATION_STATUS_GRANTED = 'granted';
export const GEO_LOCATION_STATUS_DENIED = 'denied';

const hasGeolocationAPI = () => _.get(navigator, 'geolocation.getCurrentPosition', false);

const hasPermissionsAPI = () => _.get(navigator, 'permissions.query', false);

const GEO_LOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 20000,
  maximumAge: 10 * 60 * 1000, // 10 minutes
};

const COOKIE_EXPIRES_MS = 3600 * 24 * 365 * 1000; // one year.

const emptyCallback = () => {};

const getCurrentTimestampMS = () => new Date().getTime();

const getSimulatedLocation = () => {
  const latitude = getCookie({ name: DEBUG_PANEL_CITY_LAT });
  const longitude = getCookie({ name: DEBUG_PANEL_CITY_LONG });
  const timestamp = parseInt(getCurrentTimestampMS(), 10);
  if (!latitude || !longitude) return null;
  return { latitude, longitude, timestamp };
};

const setLocationCookies = ({ latitude, longitude }) => {
  if (!latitude || !longitude) return;

  const todayTimestamp = getCurrentTimestampMS();
  const expiresMS = todayTimestamp + COOKIE_EXPIRES_MS;
  const options = {
    expires: new Date(expiresMS),
    path: '/',
  };

  setLatitude({ value: latitude, options });
  setLongitude({ value: longitude, options });
  setCookieTimeStamp({ value: todayTimestamp, options });
  setCookieModalTimeStamp({ value: todayTimestamp, options });
};

export const getLocationCookies = () => {
  const latitude = getLatitude();
  const longitude = getLongitude();
  const cookieTimestamp = getCookieTimestamp();
  if (!latitude || !longitude || !cookieTimestamp) return null;
  return {
    latitude: parseFloat(latitude),
    longitude: parseFloat(longitude),
    timestamp: parseInt(cookieTimestamp, 10),
  };
};

export const getSpoofedLocationCookies = () => {
  const latitude = getCookie({ name: DEBUG_PANEL_CITY_LAT });
  const longitude = getCookie({ name: DEBUG_PANEL_CITY_LONG });
  if (!latitude || !longitude) return null;
  return {
    latitude: parseFloat(latitude),
    longitude: parseFloat(longitude),
  };
};

const hasLocationCookieExpired = () => {
  const locationCookies = getLocationCookies();
  if (!locationCookies) return true;

  const cookieTimestamp = locationCookies.timestamp;
  const cookieExpireTimestamp = cookieTimestamp + getLocationCookieDuration();
  const todayTimestamp = getCurrentTimestampMS();
  const expired = cookieExpireTimestamp < todayTimestamp;
  return expired;
};

export const hasLocationCookies = () => {
  const locationCookies = getLocationCookies();
  if (locationCookies) {
    const latitude = locationCookies.latitude || null;
    const longitude = locationCookies.longitude || null;
    return !!(latitude && longitude);
  }
  return false;
};

const getLocationFromBrowserCoordinates = coordinates => {
  const latitude = _.get(coordinates, 'coords.latitude');
  const longitude = _.get(coordinates, 'coords.longitude');
  const timestamp = getCurrentTimestampMS();
  const location = { latitude, longitude, timestamp };
  return location;
};

const getLocationFromBrowser = cb => {
  const locationResolve = coordinates => {
    const location = getLocationFromBrowserCoordinates(coordinates);
    setLocationCookies(location);
    cb(null, location);
  };

  const locationReject = error => {
    // fallbacks to cookie location if available.
    if (hasLocationCookies()) {
      cb(null, getLocationCookies());
      return;
    }
    const errorResponse = error;
    cb(errorResponse, null);
  };

  return navigator.geolocation.getCurrentPosition(
    locationResolve,
    locationReject,
    GEO_LOCATION_OPTIONS,
  );
};

export const getCachedLocation = ({ shouldFetchLocation, cb }) => {
  const debugCookies = getSimulatedLocation();
  if (debugCookies) {
    return {
      ...debugCookies,
      debug: true,
    };
  }

  const hasCookies = hasLocationCookies();
  const hasLocationExpired = hasLocationCookieExpired();
  const locationCookies = getLocationCookies() || null;
  const shouldCheckLocation = shouldFetchLocation || (hasCookies && hasLocationExpired);
  if (!hasGeolocationAPI() || !shouldCheckLocation) return locationCookies;

  const callback = cb || emptyCallback;
  getLocationFromBrowser(callback);
  return locationCookies;
};

export const getLocation = (cb = emptyCallback) => {
  const cachedLocation = getCachedLocation({ shouldFetchLocation: true, cb });
  if (cachedLocation) {
    if (cachedLocation.debug) setLocationCookies(cachedLocation);
    cb(null, cachedLocation);
  }
};

export const getLocationPermission = (cb = emptyCallback) => {
  const debugCookies = getSimulatedLocation();
  const { geolocation_permissionsUnavailable: permissionsUnavailableErrorText } = window.Language;
  if (debugCookies) {
    cb(null, GEO_LOCATION_STATUS_GRANTED);
    return;
  }

  if (!hasPermissionsAPI()) {
    cb(permissionsUnavailableErrorText, null);
    return;
  }

  navigator.permissions
    .query({ name: 'geolocation' })
    .then(permissionStatus => {
      cb(null, permissionStatus.state);
    })
    .catch(error => {
      cb(error, null);
    });
};
